import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Play, Pause, RotateCcw, Volume2, Clock, Sparkles, Moon, Sun, Wind, Heart, ChevronRight, Send, ExternalLink, Check, X, Shield, Headphones } from 'lucide-react';
import { PrimaryButton, SecondaryButton, IconButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import AudioTip from '../components/brand/AudioTip';
import { useGuidedMeditationContent } from '../hooks/useMeditationContent';
import { CoRegulateMark } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Guided Meditations with Audio
 * Voice-guided meditation sessions using TTS
 * Content loaded from database with fallback to defaults
 */

// Icon mapping for database content
const ICON_MAP = {
    'Sun': Sun,
    'Wind': Wind,
    'Moon': Moon,
    'Heart': Heart,
    'Sparkles': Sparkles,
    'Shield': Shield
};

// Default meditations (fallback if API fails)
const DEFAULT_MEDITATIONS = [
    {
        id: 'morning-ground',
        name: 'Morning Grounding',
        description: 'Start your day centered and present',
        duration: '5 min',
        icon: Sun,
        color: '#FFB74D',
        bgColor: '#FFF8E1',
        glassVariant: 'glass-card-gold',
        script: [
            { text: "Good morning. Take a moment to arrive in this new day.", pause: 5 },
            { text: "Feel your body where it rests. The surface beneath you, supporting you.", pause: 5 },
            { text: "Take a deep breath in... and let it go slowly.", pause: 6 },
            { text: "Today is a fresh start. Whatever happened yesterday is behind you.", pause: 5 },
            { text: "Notice your feet. Imagine roots growing down into the earth, anchoring you.", pause: 6 },
            { text: "Feel the stability of the ground beneath you. You are held. You are supported.", pause: 5 },
            { text: "Bring your attention to your heart. Place a hand there if you'd like.", pause: 5 },
            { text: "Set a simple intention for today. Not a to-do list — just one word or feeling you want to carry with you.", pause: 8 },
            { text: "Take one more deep breath. Feel yourself fully present in this moment.", pause: 5 },
            { text: "When you're ready, gently open your eyes. You're ready for your day.", pause: 5 }
        ]
    },
    {
        id: 'anxiety-calm',
        name: 'Calming Anxiety',
        description: 'When worries feel overwhelming',
        duration: '7 min',
        icon: Wind,
        color: '#81C784',
        bgColor: '#FEF6EF',
        glassVariant: 'glass-card-sage',
        script: [
            { text: "Find a comfortable position. You're safe here.", pause: 5 },
            { text: "Anxiety is your nervous system trying to protect you. It's not your enemy.", pause: 5 },
            { text: "Let's slow things down. Take a breath in for four counts... one, two, three, four.", pause: 5 },
            { text: "Hold gently... one, two, three, four.", pause: 5 },
            { text: "And release slowly... one, two, three, four, five, six.", pause: 7 },
            { text: "Let's do that again. In for four...", pause: 5 },
            { text: "Hold for four...", pause: 5 },
            { text: "Out for six...", pause: 7 },
            { text: "Notice your shoulders. Let them drop away from your ears.", pause: 5 },
            { text: "Notice your jaw. Let it soften. Let your tongue rest.", pause: 5 },
            { text: "Anxiety lives in the future — in 'what ifs'. Let's come back to now.", pause: 5 },
            { text: "Right now, in this moment, you are okay. You are breathing. You are here.", pause: 6 },
            { text: "Name five things you can see around you... just notice them.", pause: 8 },
            { text: "Name four things you can feel... the texture of your clothes, the air on your skin.", pause: 8 },
            { text: "Your nervous system is calming. You're doing beautifully.", pause: 5 },
            { text: "Take one more slow breath. In... and out.", pause: 6 },
            { text: "You can return to this calm whenever you need it. It's always here.", pause: 5 }
        ]
    },
    {
        id: 'sleep-prep',
        name: 'Sleep Preparation',
        description: 'Transition into restful sleep',
        duration: '10 min',
        icon: Moon,
        color: '#7D8E95',
        bgColor: '#EDE7F6',
        glassVariant: 'glass-card-lavender',
        script: [
            { text: "Let yourself settle into your bed. Feel its support.", pause: 5 },
            { text: "The day is over. Whatever didn't get done can wait until tomorrow.", pause: 5 },
            { text: "Take a deep breath in... and let out a long sigh.", pause: 6 },
            { text: "Let your eyes close. Feel the weight of your eyelids.", pause: 5 },
            { text: "Starting at the top of your head, imagine a warm, golden light.", pause: 5 },
            { text: "This light brings deep relaxation wherever it touches.", pause: 5 },
            { text: "Feel it spreading across your scalp... your forehead smoothing...", pause: 6 },
            { text: "The light moves down to your eyes... your cheeks... your jaw releasing...", pause: 6 },
            { text: "It flows down your neck... your shoulders melting into the bed...", pause: 6 },
            { text: "Your arms grow heavy... your hands soft and still...", pause: 6 },
            { text: "The golden warmth spreads through your chest... your heartbeat slowing...", pause: 6 },
            { text: "Your belly rises and falls gently... no effort needed...", pause: 6 },
            { text: "The light continues down through your hips... your thighs...", pause: 6 },
            { text: "Your knees... your calves... your feet... all the way to your toes...", pause: 6 },
            { text: "Your whole body is now bathed in warm, golden light. Heavy and relaxed.", pause: 5 },
            { text: "With each breath, you sink deeper into rest...", pause: 6 },
            { text: "Deeper... and deeper...", pause: 6 },
            { text: "Sleep is coming. Let it take you.", pause: 8 },
            { text: "Goodnight.", pause: 5 }
        ]
    },
    {
        id: 'self-compassion',
        name: 'ADHD Reframing',
        description: 'When you need to be gentle with yourself',
        duration: '6 min',
        icon: Heart,
        color: '#F48FB1',
        bgColor: '#FCE4EC',
        glassVariant: 'glass-card-rose',
        script: [
            { text: "Place your hand over your heart. Feel its steady beat.", pause: 5 },
            { text: "You're here because something is hard right now. That's okay.", pause: 5 },
            { text: "Suffering is part of being human. You're not alone in this.", pause: 5 },
            { text: "Think of someone who loves you unconditionally. A person, a pet, anyone.", pause: 6 },
            { text: "Imagine them looking at you with complete acceptance and warmth.", pause: 5 },
            { text: "Now, try to look at yourself the same way.", pause: 6 },
            { text: "Repeat silently: May I be kind to myself.", pause: 6 },
            { text: "May I accept myself as I am.", pause: 6 },
            { text: "May I forgive myself for my mistakes.", pause: 6 },
            { text: "May I be patient with my struggles.", pause: 6 },
            { text: "You are doing the best you can with what you have.", pause: 5 },
            { text: "That is enough. You are enough.", pause: 6 },
            { text: "Take a breath and feel the warmth of your own hand on your heart.", pause: 5 },
            { text: "You deserve the same compassion you give to others.", pause: 5 },
            { text: "Carry this kindness with you.", pause: 5 }
        ]
    },
    {
        id: 'adhd-focus',
        name: 'ADHD Focus Reset',
        description: 'Clear mental clutter and find your center',
        duration: '4 min',
        icon: Sparkles,
        color: '#4FC3F7',
        bgColor: '#E1F5FE',
        glassVariant: 'glass-card-blue',
        script: [
            { text: "Your ADHD brain has a lot going on. Let's create some space.", pause: 5 },
            { text: "First, shake your hands for a few seconds. Get the fidgets out.", pause: 6 },
            { text: "Good. Now take a breath. In through your nose... out through your mouth.", pause: 6 },
            { text: "Imagine your thoughts as browser tabs. There are a lot of them open.", pause: 5 },
            { text: "We're not closing them. We're just minimizing them for now.", pause: 5 },
            { text: "Watch them shrink down... one by one... still there, just smaller.", pause: 6 },
            { text: "Now there's space on your mental desktop.", pause: 5 },
            { text: "Think of the ONE thing you want to focus on next. Just one.", pause: 6 },
            { text: "Picture it clearly. What does starting look like?", pause: 6 },
            { text: "Take a breath and send energy to that one thing.", pause: 5 },
            { text: "You don't have to do it perfectly. You just have to start.", pause: 5 },
            { text: "Three, two, one... go do your thing.", pause: 4 }
        ]
    },
    {
        id: 'rsd-healing',
        name: 'RSD Meditation Hypnosis',
        description: 'Heal rejection sensitivity and reclaim your sense of self-worth',
        duration: '20 min',
        icon: Shield,
        color: '#9575CD',
        bgColor: '#EDE7F6',
        glassVariant: 'glass-card-lavender',
        script: [
            // Opening relaxation - lots of pauses for settling in
            { text: "Begin by finding a comfortable position.", pause: 6 },
            { text: "It could be sitting.", pause: 2 },
            { text: "Or laying down.", pause: 4 },
            { text: "You may like to grab a blanket.", pause: 6 },
            { text: "Make any final adjustments to ensure you will be comfortable throughout this process.", pause: 8 },
            { text: "And when you are comfortable, closing your eyes.", pause: 8 },
            { text: "Allow them now to gently rest.", pause: 5 },
            { text: "And take a nice long slow deep breath in through your mouth.", pause: 5 },
            { text: "And hold that breath for the mental count of three.", pause: 6 },
            { text: "Then exhale it slowly out of your mouth.", pause: 6 },
            { text: "And relax your body.", pause: 7 },
            { text: "Ensure that your feet are both flat on the floor.", pause: 8 },
            { text: "Become aware of the surface beneath you now.", pause: 4 },
            { text: "And feel your feet becoming heavy.", pause: 5 },
            { text: "Very heavy.", pause: 5 },
            { text: "Comfortable and so relaxed. And let go.", pause: 8 },
            { text: "Allow your hands to relax and feel heavy.", pause: 3 },
            { text: "Let them relax.", pause: 5 },
            { text: "And let go.", pause: 8 },
            { text: "And take another long, slow, deep breath.", pause: 2 },
            { text: "Hold it for three.", pause: 8 },
            { text: "And breathe out.", pause: 6 },
            { text: "Calmness and relaxation is beginning to flow down your body now.", pause: 6 },
            { text: "From the top of your head.", pause: 2 },
            { text: "All the way down to the tips of the toes.", pause: 5 },
            { text: "Every nerve. Every cell. Every fibre. Every consciousness of your body.", pause: 3 },
            { text: "Is relaxing nicely now. Relaxing. Letting go of tension. Letting go. Let go.", pause: 6 },
            { text: "And take just one more long, slow deep breath and hold it for three.", pause: 8 },
            { text: "And let go.", pause: 8 },
            { text: "Notice how heavy your hands are becoming now. So heavy.", pause: 8 },
            { text: "Feel it spreading all the way over your body.", pause: 9 },
            { text: "Let your eyes feel heavy as well.", pause: 4 },
            { text: "Heavy and comfortable and so relaxed.", pause: 3 },
            { text: "Let go of those muscles around your eyes.", pause: 3 },
            { text: "For it takes no effort whatsoever to allow them to remain comfortably closed.", pause: 5 },
            { text: "Imagine little weights pulling down on your lashes.", pause: 5 },
            { text: "Bring your awareness to your mouth and tongue.", pause: 4 },
            { text: "Allow all the little muscles around your mouth to relax.", pause: 6 },
            { text: "Relax your tongue, making sure it is not pressing on the roof of your mouth.", pause: 8 },
            { text: "Allow your eyes to remain closed and allow that relaxing feeling to flow to the top of your head.", pause: 3 },
            { text: "Swirling in your brain, collecting any stress and tension.", pause: 5 },
            { text: "Allow your brain to relax.", pause: 7 },
            { text: "Feel it spread down to your shoulders.", pause: 6 },
            { text: "Down.", pause: 4 },
            { text: "Down to your chest.", pause: 5 },
            { text: "Down to your stomach.", pause: 5 },
            { text: "Feel this feeling swirl around your stomach, removing any tension that may be stored there.", pause: 8 },
            { text: "And feel this relaxing comfort continue to move down both your legs.", pause: 5 },
            { text: "Moving into your thighs.", pause: 3 },
            { text: "Your knees.", pause: 3 },
            { text: "Down. Down into your feet.", pause: 8 },
            { text: "Feel each of your toes relax.", pause: 5 },
            { text: "You are feeling so very relaxed.", pause: 5 },
            { text: "Enjoy this feeling.", pause: 5 },
            { text: "You have nowhere to be.", pause: 5 },
            { text: "Nothing to do other than enjoy this peaceful comfort.", pause: 8 },
            
            // Transition to subconscious work
            { text: "And as you float in this peaceful space, your subconscious mind is open and receptive to what it is about to hear.", pause: 5 },
            { text: "Ready to absorb these words with no effort at all.", pause: 3 },
            { text: "Absorbing these words like a sponge.", pause: 6 },
            
            // Core RSD healing content
            { text: "I want you to know that you are not broken.", pause: 8 },
            { text: "Let those words exist somewhere inside you.", pause: 6 },
            { text: "Even if a part of you wants to argue with them.", pause: 4 },
            { text: "I want you to ignore this part of you for now.", pause: 4 },
            { text: "You are not broken.", pause: 5 },
            { text: "You are not defective.", pause: 6 },
            { text: "You were never too much.", pause: 4 },
            { text: "Too sensitive.", pause: 3 },
            { text: "Too emotional.", pause: 2 },
            { text: "Too intense.", pause: 2 },
            { text: "Or not enough.", pause: 5 },
            { text: "What you have is a subconscious mind that learned very early to stay alert and keep away from danger.", pause: 3 },
            { text: "In the form of rejection.", pause: 3 },
            { text: "Disapproval.", pause: 2 },
            { text: "Misunderstanding.", pause: 2 },
            { text: "Or emotional withdrawal.", pause: 6 },
            { text: "And that learning made sense at the time.", pause: 5 },
            { text: "Your mind adapted to what it saw around it.", pause: 5 },
            { text: "Your sensitivity developed because it had to.", pause: 6 },
            { text: "It developed because you were different in ways that were often unseen.", pause: 4 },
            { text: "Or misunderstood.", pause: 2 },
            { text: "Because your brain works quickly.", pause: 3 },
            { text: "Creatively.", pause: 2 },
            { text: "Associatively.", pause: 2 },
            { text: "Emotionally.", pause: 3 },
            { text: "It developed this way because you noticed everything and you felt deeply about things.", pause: 4 },
            { text: "And because feedback, tone, facial expressions, pauses, silences and changes in energy mattered deeply to you.", pause: 4 },
            { text: "Long before anyone explained why.", pause: 6 },
            { text: "When rejection happened.", pause: 5 },
            { text: "Whether that was real or perceived.", pause: 5 },
            { text: "It didn't just feel unpleasant.", pause: 6 },
            { text: "It felt devastating.", pause: 5 },
            { text: "It felt like a threat to your belonging.", pause: 4 },
            { text: "To your safety.", pause: 3 },
            { text: "To your right to exist comfortably in connection with others.", pause: 6 },
            { text: "So, the fear of rejection didn't come from weakness.", pause: 4 },
            { text: "It came from being corrected more often than praised.", pause: 5 },
            { text: "From being told to try harder when you already were.", pause: 5 },
            { text: "From being misunderstood.", pause: 5 },
            { text: "From being laughed at.", pause: 2 },
            { text: "Ignored.", pause: 2 },
            { text: "Excluded.", pause: 2 },
            { text: "Or subtly dismissed.", pause: 3 },
            { text: "From being told directly or indirectly that who you were was not right.", pause: 6 },
            { text: "And over time.", pause: 4 },
            { text: "You started to believe that rejection was dangerous.", pause: 3 },
            { text: "Something to be prevented at all costs.", pause: 4 },
            { text: "So, you learned to scan.", pause: 2 },
            { text: "To anticipate.", pause: 2 },
            { text: "To replay conversations.", pause: 2 },
            { text: "And to brace for impact.", pause: 5 },
            { text: "You learned to feel emotional pain quickly and intensely and protect yourself through vigilance.", pause: 5 },
            { text: "And today, you are here not to erase that part of you, but to update it.", pause: 6 },
            { text: "Because you are no longer that child who had no context.", pause: 4 },
            { text: "No language.", pause: 2 },
            { text: "And no support for their experience.", pause: 6 },
            { text: "Your subconscious mind can learn new rules.", pause: 3 },
            { text: "Gently.", pause: 2 },
            { text: "Respectfully.", pause: 2 },
            { text: "And easily.", pause: 5 },
            { text: "So, allow this to be a conversation with the part of you that fears rejection.", pause: 6 },
            { text: "That part of you is in your subconscious mind now and is listening.", pause: 6 },
            { text: "And you might have a feeling that as you listen to these words, something inside you is already paying attention.", pause: 6 },
            { text: "Perhaps you notice a sense of recognition.", pause: 3 },
            { text: "Perhaps emotion.", pause: 3 },
            { text: "Maybe resistance.", pause: 4 },
            { text: "All of these things are welcome here.", pause: 4 },
            { text: "Because RSD is not just an emotional reaction.", pause: 5 },
            { text: "It is a body-based memory.", pause: 5 },
            { text: "It exists in the tightening of the chest.", pause: 3 },
            { text: "In the sinking of the stomach.", pause: 3 },
            { text: "In the heat behind the eyes.", pause: 5 },
            { text: "It exists in the sudden drop when a message goes unanswered.", pause: 4 },
            { text: "In the spike when someone's tone of voice changes.", pause: 3 },
            { text: "And in the devastation that you experience when criticism appears.", pause: 6 },
            { text: "So, as we talk about these reactions, we speak to the body as well as the mind.", pause: 6 },
            { text: "And we begin by acknowledging something important.", pause: 4 },
            { text: "Your sensitivity is not the problem.", pause: 4 },
            { text: "Sensitivity is information.", pause: 3 },
            { text: "Sensitivity is perception.", pause: 3 },
            { text: "Sensitivity is attunement.", pause: 4 },
            { text: "It keeps you alive.", pause: 5 },
            { text: "The pain came from being sensitive in environments that did not know how to hold sensitivity safely.", pause: 7 },
            
            // Guided visualization
            { text: "Bring awareness to that part of you that fears rejection.", pause: 6 },
            { text: "Really try and bring this part forward.", pause: 5 },
            { text: "Maybe you might like to remember a time you felt rejected.", pause: 7 },
            { text: "You don't need to push it away.", pause: 5 },
            { text: "You are safe.", pause: 6 },
            { text: "You don't need to analyse it.", pause: 3 },
            { text: "Just notice it.", pause: 5 },
            { text: "You are in control of this experience.", pause: 7 },
            { text: "And now imagine that another part of you is also present.", pause: 5 },
            { text: "This other part is wiser and calmer.", pause: 5 },
            { text: "This part understands rejection sensitivity.", pause: 5 },
            { text: "This part understands your nervous system and your subconscious mind and knows context.", pause: 5 },
            { text: "And this part begins to speak.", pause: 4 },
            { text: "It says:", pause: 2 },
            { text: "Of course you're afraid.", pause: 4 },
            { text: "Look at what you've been through.", pause: 6 },
            { text: "And something in your mind may relax slightly.", pause: 3 },
            { text: "Because fear that is acknowledged does not need to scream as loudly.", pause: 6 },
            { text: "This wiser part continues.", pause: 5 },
            { text: "You learned to associate feedback with danger.", pause: 4 },
            { text: "You learned to associate distance with abandonment.", pause: 4 },
            { text: "You learned to associate mistakes with rejection.", pause: 5 },
            { text: "And none of that means you were wrong.", pause: 5 },
            { text: "It means you were responding to the information that you had at the time.", pause: 5 },
            { text: "And now, slowly, gently, new information is introduced.", pause: 6 },
            { text: "Because today, you realise that rejection is not the same as danger.", pause: 6 },
            { text: "And your subconscious mind can learn the difference.", pause: 5 },
            { text: "Through repeated experiences of safety.", pause: 5 },
            { text: "So, allow yourself to notice in this moment.", pause: 5 },
            { text: "Nothing is being demanded of you.", pause: 5 },
            { text: "Nothing is being judged.", pause: 5 },
            { text: "You are simply here.", pause: 5 },
            { text: "And your body is receiving the message that it can exist without being evaluated.", pause: 6 },
            { text: "That alone is enough to make some big corrections in your mind.", pause: 6 },
            
            // Reframing ambiguity
            { text: "Now consider this.", pause: 5 },
            { text: "Rejection is often ambiguous.", pause: 4 },
            { text: "Silence can mean many things.", pause: 4 },
            { text: "A facial expression can reflect someone else's internal world.", pause: 4 },
            { text: "Someone's reaction is about them.", pause: 4 },
            { text: "And is not yours to hold.", pause: 5 },
            { text: "A pause can be about them feeling overwhelmed.", pause: 2 },
            { text: "Distracted.", pause: 2 },
            { text: "Fatigued.", pause: 2 },
            { text: "Or maybe the timing is simply not right for them.", pause: 5 },
            { text: "But RSD falsely turns ambiguity into certainty.", pause: 6 },
            { text: "It says:", pause: 2 },
            { text: "They don't want me.", pause: 3 },
            { text: "I did something wrong.", pause: 3 },
            { text: "I am too much.", pause: 3 },
            { text: "I am not enough.", pause: 4 },
            { text: "And those statements feel like facts.", pause: 5 },
            
            // New coping technique
            { text: "So, from now on, I want you to pause for a moment when you hear words that could be interpreted like this.", pause: 6 },
            { text: "Take a pause when you feel those familiar feelings start to rise in your chest.", pause: 4 },
            { text: "And just notice them.", pause: 4 },
            { text: "Just like you have done here today.", pause: 5 },
            { text: "And in that pause, take a deep breath in.", pause: 3 },
            { text: "Hold it for the count of four.", pause: 5 },
            { text: "And then slowly exhale that breath.", pause: 3 },
            { text: "Feeling all the negativity of your thoughts being released from your body as you breathe out.", pause: 7 },
            { text: "Instead of collapsing into RSD.", pause: 5 },
            { text: "There is choice.", pause: 4 },
            { text: "You have new awareness.", pause: 6 },
            { text: "You might not choose this every time.", pause: 3 },
            { text: "And that's okay.", pause: 4 },
            { text: "Because change does not require perfection.", pause: 4 },
            { text: "It requires permission.", pause: 5 },
            { text: "Permission for your subconscious mind to learn gradually.", pause: 5 },
            { text: "And each time when you pause for a moment before reacting, it reinforces that pattern of behaviour.", pause: 4 },
            { text: "So that before long, it becomes a habit.", pause: 3 },
            { text: "A positive habit that helps you to react differently.", pause: 5 },
            
            // Resilience not numbness
            { text: "Now notice something important.", pause: 3 },
            { text: "You are allowed to be affected by others.", pause: 4 },
            { text: "RSD does not mean you should feel nothing.", pause: 5 },
            { text: "It means you feel things intensely.", pause: 5 },
            { text: "The goal is not numbness.", pause: 3 },
            { text: "The goal is resilience.", pause: 4 },
            { text: "The goal is understanding.", pause: 5 },
            { text: "A new perspective.", pause: 5 },
            { text: "It is the ability to feel and then recover.", pause: 3 },
            { text: "The ability to experience emotion without losing your sense of self.", pause: 6 },
            { text: "And your sense of self is deeper than approval.", pause: 7 },
            { text: "You exist even when someone misunderstands you.", pause: 4 },
            { text: "You exist even when someone is disappointed.", pause: 5 },
            { text: "You exist even when someone leaves.", pause: 6 },
            { text: "Your worth does not fluctuate with feedback.", pause: 5 },
            { text: "It never did.", pause: 3 },
            { text: "It just felt like it did, because attachment mattered.", pause: 5 },
            { text: "And attachment still matters.", pause: 5 },
            { text: "So today, we teach your subconscious mind a new truth:", pause: 3 },
            { text: "Connection can be important without being life-or-death.", pause: 6 },
            { text: "You can care without collapsing.", pause: 5 },
            { text: "You can value relationships without disappearing inside them.", pause: 6 },
            
            // Separating past from present
            { text: "And now, gently, we can begin to separate the past from the present.", pause: 6 },
            { text: "The body often reacts as if every rejection is the first rejection.", pause: 4 },
            { text: "But you are not that young anymore.", pause: 6 },
            { text: "You have felt rejection in your past and you survived.", pause: 6 },
            { text: "There is nothing wrong with you.", pause: 5 },
            { text: "That past belief that rejection means something is wrong with you.", pause: 4 },
            { text: "This belief is old and false.", pause: 5 },
            { text: "You do not need to earn belonging by shrinking into the shadows.", pause: 4 },
            { text: "You belong.", pause: 5 },
            { text: "When rejection happens, it is someone else's opinion only.", pause: 3 },
            { text: "It comes from the limited information that they have.", pause: 4 },
            { text: "Their internal world.", pause: 4 },
            { text: "Their wounds and the traumas they have suffered.", pause: 5 },
            { text: "And sometimes it hurts.", pause: 3 },
            { text: "But you can survive that hurt.", pause: 3 },
            { text: "You can let it wash over you and carry on.", pause: 3 },
            { text: "You have already survived this in the past and you can do so again.", pause: 6 },
            
            // Affirmations
            { text: "Allow these messages to sink deeply into your subconscious mind:", pause: 5 },
            { text: "You are allowed to exist without constant self-monitoring.", pause: 5 },
            { text: "You are allowed to take up space.", pause: 5 },
            { text: "You are allowed to be impacted and still be okay.", pause: 5 },
            { text: "You still feel deeply, but now you are less impacted by others' words and actions.", pause: 5 },
            { text: "Because you know, deep down, that your worth is not dependent upon anyone else.", pause: 4 },
            { text: "You are worthy just because you are you.", pause: 8 },
            
            // Awakening
            { text: "As you come out of this deeply relaxed state, you will awaken with a sense of clarity and perspective.", pause: 5 },
            { text: "Knowing that your sensitivity is not your enemy.", pause: 4 },
            { text: "It is a strength learning how to feel safe.", pause: 5 },
            { text: "And that learning is happening now.", pause: 3 },
            { text: "Slowly.", pause: 2 },
            { text: "Gently.", pause: 2 },
            { text: "And in your own time.", pause: 4 },
            { text: "For you have embarked on a journey of self-discovery and empowerment that opens the door to a happier, healthier, and more fulfilling life.", pause: 5 },
            { text: "And these suggestions are firmly embedded in your subconscious mind and grow stronger and stronger, day by day.", pause: 5 },
            { text: "They grow stronger by the day.", pause: 2 },
            { text: "Stronger by the hour.", pause: 2 },
            { text: "Stronger by the minute.", pause: 4 },
            { text: "In a moment, I will count the numbers from one to five.", pause: 3 },
            { text: "And at the count of five, you will be wide awake, fully alert and refreshed, and ready to allow these suggestions to work for you.", pause: 5 },
            { text: "So, when you are ready, start coming back:", pause: 4 },
            { text: "One.", pause: 3 },
            { text: "Two.", pause: 3 },
            { text: "Three. Coming slowly back.", pause: 3 },
            { text: "Four. Eyelids beginning to flutter.", pause: 3 },
            { text: "And five. Eyes open. Wide awake. Mind and body returning to normal conscious awareness.", pause: 5 }
        ]
    }
];

const GuidedMeditations = () => {
    const navigate = useNavigate();
    
    // Fetch content from database (falls back to defaults)
    const { meditations: dbMeditations, loading: contentLoading } = useGuidedMeditationContent(DEFAULT_MEDITATIONS);
    
    // Map icons from string to component and filter out hidden meditations
    // HIDDEN: 'adhd-focus' - Temporarily hidden until custom hypnotherapy script is added
    // AVAILABLE: 'anxiety-calm' - Audio cache complete (60/60 segments) ✅
    // AVAILABLE: 'rsd-healing' - Audio cache complete (278/278 segments) ✅
    const HIDDEN_MEDITATION_IDS = ['adhd-focus'];
    
    const MEDITATIONS = dbMeditations
        .filter(med => !HIDDEN_MEDITATION_IDS.includes(med.id))
        .map(med => ({
            ...med,
            icon: typeof med.icon === 'string' ? (ICON_MAP[med.icon] || Sparkles) : med.icon
        }));
    
    const [selectedMeditation, setSelectedMeditation] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [currentSegment, setCurrentSegment] = useState(0);
    const [isLoadingAudio, setIsLoadingAudio] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [musicEnabled, setMusicEnabled] = useState(true);
    const [musicVolume] = useState(0.35);
    
    // URL params for deep linking (e.g., ?meditation=rsd-healing)
    const [searchParams] = useSearchParams();
    
    // Auto-select meditation from URL param
    useEffect(() => {
        const meditationParam = searchParams.get('meditation');
        if (meditationParam && MEDITATIONS.find(m => m.id === meditationParam)) {
            setSelectedMeditation(meditationParam);
        }
    }, [searchParams, MEDITATIONS]);
    
    // Hypnotherapy enquiry state
    const [showEnquiryModal, setShowEnquiryModal] = useState(false);
    const [enquirySubmitted, setEnquirySubmitted] = useState(false);
    const [enquiryForm, setEnquiryForm] = useState({
        name: '',
        email: '',
        phone: '',
        concerns: '',
        preferredContact: 'email'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const audioRef = useRef(null);
    const musicRef = useRef(null);
    const timerRef = useRef(null);
    
    // Audio cache for pre-generated segments
    const audioCacheRef = useRef({});

    const meditation = selectedMeditation ? MEDITATIONS.find(m => m.id === selectedMeditation) : null;

    // Background music control
    useEffect(() => {
        if (musicRef.current) {
            musicRef.current.volume = musicVolume;
            musicRef.current.loop = true;
        }
    }, [musicVolume]);

    // Start/stop background music with session
    useEffect(() => {
        if (musicRef.current && musicEnabled) {
            if (isPlaying && !isPaused) {
                musicRef.current.play().catch(() => {});
            } else {
                musicRef.current.pause();
            }
        }
    }, [isPlaying, isPaused, musicEnabled]);

    // Lower music volume during voice
    useEffect(() => {
        if (musicRef.current && musicEnabled) {
            if (isLoadingAudio || (audioRef.current && !audioRef.current.paused)) {
                musicRef.current.volume = musicVolume * 0.3;
            } else {
                musicRef.current.volume = musicVolume;
            }
        }
    }, [isLoadingAudio, musicEnabled, musicVolume]);

    // Generate cache key matching backend format (MD5 hash)
    const getCacheKey = async (text, stability = 0.5, similarity = 0.75) => {
        // Use the same format as backend: text:voice_id:stability:similarity
        const voiceId = 'TclCHx3LMj6HHtDQbpvu'; // Default ElevenLabs voice ID
        const content = `${text}:${voiceId}:${stability}:${similarity}`;
        
        // Simple hash that matches MD5 output format
        const encoder = new TextEncoder();
        const data = encoder.encode(content);
        const hashBuffer = await crypto.subtle.digest('MD5', data).catch(() => null);
        
        if (hashBuffer) {
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        }
        
        // Fallback: simple hash
        let hash = 0;
        for (let i = 0; i < content.length; i++) {
            const char = content.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return Math.abs(hash).toString(16);
    };

    // Generate and play audio for current segment (with cache check)
    const playSegment = async (segmentIndex) => {
        if (!meditation || segmentIndex >= meditation.script.length) {
            setIsComplete(true);
            setIsPlaying(false);
            return;
        }

        const segment = meditation.script[segmentIndex];
        setIsLoadingAudio(true);

        try {
            // Check if we have this segment cached in memory (session cache)
            const memoryCacheKey = `${meditation.id}_${segmentIndex}`;
            let audioBase64 = audioCacheRef.current[memoryCacheKey];
            
            if (!audioBase64) {
                // Try to fetch from MongoDB cache first (pre-generated audio)
                try {
                    // Generate the cache key the same way backend does
                    const voiceId = 'TclCHx3LMj6HHtDQbpvu';
                    const content = `${segment.text}:${voiceId}:0.5:0.75`;
                    
                    // Create MD5-like hash (we'll use the backend endpoint which handles this)
                    const cacheResponse = await fetch(`${API_URL}/api/audio-cache/by-text`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            text: segment.text,
                            meditation_id: meditation.id,
                            segment_index: segmentIndex
                        })
                    });
                    
                    if (cacheResponse.ok) {
                        const cachedData = await cacheResponse.json();
                        if (cachedData.audio) {
                            audioBase64 = cachedData.audio;
                            console.log(`Loaded cached audio for segment ${segmentIndex}`);
                        }
                    }
                } catch (e) {
                    console.log('Cache fetch failed, will generate:', e.message);
                }
                
                // If not in cache, generate audio via TTS (uses ElevenLabs credits)
                if (!audioBase64) {
                    console.log(`Generating audio for segment ${segmentIndex} (not cached)`);
                    const response = await fetch(`${API_URL}/api/tts/generate`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            text: segment.text,
                            voice: 'alloy',
                            speed: 0.85
                        })
                    });

                    if (response.ok) {
                        const data = await response.json();
                        audioBase64 = data.audio;
                    }
                }
                
                // Cache in memory for this session
                if (audioBase64) {
                    audioCacheRef.current[memoryCacheKey] = audioBase64;
                }
            }
            
            if (audioBase64 && audioRef.current) {
                audioRef.current.src = `data:audio/mp3;base64,${audioBase64}`;
                
                audioRef.current.onended = () => {
                    // Wait for the pause duration, then play next segment
                    timerRef.current = setTimeout(() => {
                        if (!isPaused) {
                            setCurrentSegment(segmentIndex + 1);
                            playSegment(segmentIndex + 1);
                        }
                    }, segment.pause * 1000);
                };
                
                audioRef.current.play();
            }
        } catch (error) {
            console.error('Audio generation failed:', error);
            // Continue to next segment even if audio fails
            timerRef.current = setTimeout(() => {
                setCurrentSegment(segmentIndex + 1);
                playSegment(segmentIndex + 1);
            }, 3000);
        } finally {
            setIsLoadingAudio(false);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
        };
    }, []);

    const handleStart = () => {
        setIsPlaying(true);
        setIsPaused(false);
        setCurrentSegment(0);
        playSegment(0);
    };

    const handlePause = () => {
        if (isPaused) {
            setIsPaused(false);
            if (audioRef.current) audioRef.current.play();
        } else {
            setIsPaused(true);
            if (audioRef.current) audioRef.current.pause();
            if (timerRef.current) clearTimeout(timerRef.current);
        }
    };

    const handleReset = () => {
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentSegment(0);
        setIsComplete(false);
        if (timerRef.current) clearTimeout(timerRef.current);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = '';
        }
        if (musicRef.current) {
            musicRef.current.pause();
            musicRef.current.currentTime = 0;
        }
    };

    const handleBack = () => {
        if (selectedMeditation) {
            handleReset();
            setSelectedMeditation(null);
        } else {
            navigate(-1);
        }
    };

    // Selection screen
    if (!selectedMeditation) {
        return (
            <div className="min-h-screen bg-transparent">
                <Helmet>
                    <title>Guided Meditations & Hypnotherapy | CoRegulate ADHD</title>
                    <meta name="description" content="Voice-guided meditation sessions designed for ADHD minds. Includes RSD (Rejection Sensitivity Dysphoria) healing meditation, anxiety relief, self-compassion, and emotional regulation sessions using clinical hypnotherapy techniques." />
                    <meta name="keywords" content="ADHD meditation, RSD meditation, rejection sensitivity dysphoria, hypnotherapy for ADHD, guided meditation, emotional regulation, anxiety relief, self-compassion meditation" />
                    <meta property="og:title" content="Guided Meditations & Hypnotherapy | CoRegulate ADHD" />
                    <meta property="og:description" content="Voice-guided meditation sessions designed for ADHD minds. Includes RSD healing, anxiety relief, and emotional regulation using clinical hypnotherapy techniques." />
                    <meta property="og:type" content="website" />
                    <link rel="canonical" href="https://coregulateadhd.com/regulate/guided-meditations" />
                </Helmet>
                
                <header className="sticky top-0 z-40 glass-header p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <IconButton onClick={() => navigate(-1)} data-testid="back-btn" className="text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#FFBB98]/40 transition-all duration-200">
                                <ArrowLeft className="w-5 h-5" />
                            </IconButton>
                            <div>
                                <Heading level={3} className="text-[#1D6B6B]">
                                    Guided Meditations
                                </Heading>
                                <SmallText className="text-[#7D8E95]">
                                    Voice-guided sessions for different needs
                                </SmallText>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/home')}
                            className="hover:opacity-80 transition-opacity"
                            data-testid="header-logo"
                            aria-label="Go to dashboard"
                        >
                            <CoRegulateMark size={32} />
                        </button>
                    </div>
                </header>

                <main className="max-w-2xl mx-auto px-4 py-8">
                    {/* Intro text */}
                    <div className="text-center mb-6">
                        <p className="text-[#7D8E95] text-sm sm:text-base leading-relaxed max-w-md mx-auto">
                            Find a comfortable position, put on headphones if you have them, and let the voice guide you.
                        </p>
                    </div>

                    {/* Educational section about hypnotherapy */}
                    <div className="glass-card rounded-2xl p-5 mb-6">
                        <Heading level={4} className="text-[#1D6B6B] mb-3 text-base sm:text-lg">
                            Why These Sessions Work
                        </Heading>
                        <div className="space-y-3 text-[#7D8E95] text-sm leading-relaxed">
                            <p>
                                These guided sessions use <strong className="text-[#1D6B6B]">clinical hypnotherapy techniques</strong> — the same methods used by therapists to help with anxiety, emotional regulation, and building new neural pathways.
                            </p>
                            <p>
                                As you follow along, you may enter a <strong className="text-[#1D6B6B]">light hypnotic state</strong> — a natural, deeply relaxed state where your mind becomes more receptive to positive suggestions. This is completely safe and you remain in control throughout.
                            </p>
                            <p>
                                Hypnosis works by bypassing the critical, overthinking part of your brain (often overactive in ADHD) and speaking directly to your subconscious — where lasting change happens.
                            </p>
                        </div>
                        <div className="mt-3 p-3 bg-[#FFBB98]/10 rounded-xl border border-white/40/20">
                            <SmallText className="text-[#7D8E95] text-xs">
                                <strong className="text-[#E89060]">Note:</strong> These sessions may induce a light hypnotic trance. Avoid listening while driving or operating machinery. Find a safe, comfortable space where you can fully relax.
                            </SmallText>
                        </div>
                    </div>

                    {/* Meditation cards - glass-morphism design */}
                    <div className="grid gap-4">
                        {MEDITATIONS.map((med, index) => {
                            const Icon = med.icon;
                            return (
                                <button
                                    key={med.id}
                                    onClick={() => setSelectedMeditation(med.id)}
                                    className={`w-full ${med.glassVariant || 'glass-card'} rounded-2xl p-5 text-left card-hover hover-shine brand-slide-up`}
                                    style={{ 
                                        animationDelay: `${index * 60}ms`
                                    }}
                                    data-testid={`meditation-${med.id}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div 
                                            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: med.color + '25', boxShadow: `0 4px 12px -4px ${med.color}40` }}
                                        >
                                            <Icon className="w-6 h-6" style={{ color: med.color }} />
                                        </div>
                                        <div className="flex-1">
                                            <Heading level={4} className="text-[#1D6B6B] mb-1 text-base">
                                                {med.name}
                                            </Heading>
                                            <SmallText className="text-[#7D8E95] mb-2">
                                                {med.description}
                                            </SmallText>
                                            <div className="flex items-center gap-2 text-[#7D8E95]">
                                                <Clock className="w-3.5 h-3.5" />
                                                <span className="text-xs font-medium">{med.duration}</span>
                                            </div>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-[#7D8E95]" />
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Coming soon hint */}
                    <div className="mt-8 text-center">
                        <p className="text-[#7D8E95] text-xs">
                            More meditations coming soon
                        </p>
                    </div>
                </main>
            </div>
        );
    }

    // Completion screen
    const handleEnquirySubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            // Send enquiry to backend
            const response = await fetch(`${API_URL}/api/hypnotherapy-enquiry`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...enquiryForm,
                    source: 'coregulate_meditation_completion',
                    meditationCompleted: meditation?.name || 'Unknown'
                })
            });
            
            if (response.ok) {
                setEnquirySubmitted(true);
            }
        } catch (error) {
            console.error('Enquiry submission failed:', error);
            // Still show success - we'll handle this gracefully
            setEnquirySubmitted(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isComplete) {
        const Icon = meditation.icon;
        return (
            <div className="min-h-screen" style={{ backgroundColor: meditation.bgColor }}>
                <header className="p-4">
                    <IconButton onClick={handleBack} data-testid="close-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                </header>
                
                <main className="max-w-md mx-auto px-6 py-8 text-center">
                    <div 
                        className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                        style={{ backgroundColor: meditation.color + '30' }}
                    >
                        <Icon className="w-10 h-10" style={{ color: meditation.color }} />
                    </div>
                    
                    <Heading level={1} className="text-[#1D6B6B] mb-4">
                        Session Complete
                    </Heading>
                    
                    <BodyText className="text-[#7D8E95] mb-6 leading-relaxed">
                        Well done. Take a moment to notice how you feel now compared to when you started.
                    </BodyText>
                    
                    <div className="space-y-3 mb-8">
                        <PrimaryButton onClick={() => navigate('/home')} className="w-full">
                            Return to Dashboard
                        </PrimaryButton>
                        <SecondaryButton onClick={handleReset} className="w-full">
                            Repeat Session
                        </SecondaryButton>
                    </div>

                    {/* Hypnotherapy Info Card */}
                    <div className="glass-card rounded-2xl p-6 text-left">
                        <BodyText className="text-[#7D8E95] leading-relaxed mb-4">
                            If you found this hypnotic meditation helpful and are interested in diving deeper into hypnotherapy, we can help.
                        </BodyText>
                        <button
                            onClick={() => setShowEnquiryModal(true)}
                            className="inline-flex items-center gap-2 text-[#E89060] font-medium hover:underline"
                            data-testid="learn-more-hypnotherapy-btn"
                        >
                            Learn more
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </main>

                {/* Hypnotherapy Modal */}
                {showEnquiryModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        {/* Backdrop */}
                        <div 
                            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                            onClick={() => !enquirySubmitted && setShowEnquiryModal(false)}
                        />
                        
                        {/* Modal Content */}
                        <div className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-scale-up">
                            {/* Close button */}
                            <button
                                onClick={() => setShowEnquiryModal(false)}
                                className="absolute top-4 right-4 p-2 text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                                data-testid="close-modal-btn"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            {!enquirySubmitted ? (
                                <div className="p-8">
                                    {/* Header */}
                                    <div className="text-center mb-6">
                                        <div className="w-16 h-16 rounded-full bg-[#FFBB98]/20 flex items-center justify-center mx-auto mb-4">
                                            <Heart className="w-8 h-8 text-[#E89060]" />
                                        </div>
                                        <Heading level={2} className="text-[#1D6B6B] mb-2">
                                            Explore Clinical Hypnotherapy
                                        </Heading>
                                        <BodyText className="text-[#7D8E95]">
                                            Take your healing journey further with personalised 1:1 sessions
                                        </BodyText>
                                    </div>

                                    {/* About Section */}
                                    <div className="bg-white/70 rounded-2xl p-5 mb-6">
                                        <Heading level={4} className="text-[#1D6B6B] mb-3">
                                            What is Clinical Hypnotherapy?
                                        </Heading>
                                        <BodyText className="text-[#7D8E95] text-sm leading-relaxed mb-3">
                                            Clinical hypnotherapy is a powerful, evidence-based approach that works with your subconscious mind to create lasting change. It's particularly effective for:
                                        </BodyText>
                                        <ul className="text-[#7D8E95] text-sm space-y-2">
                                            <li className="flex items-start gap-2">
                                                <Check className="w-4 h-4 text-[#81C784] mt-0.5 flex-shrink-0" />
                                                <span>Anxiety and emotional regulation</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <Check className="w-4 h-4 text-[#81C784] mt-0.5 flex-shrink-0" />
                                                <span>ADHD-related challenges</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <Check className="w-4 h-4 text-[#81C784] mt-0.5 flex-shrink-0" />
                                                <span>Trauma and limiting beliefs</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <Check className="w-4 h-4 text-[#81C784] mt-0.5 flex-shrink-0" />
                                                <span>Confidence and self-esteem</span>
                                            </li>
                                        </ul>
                                    </div>

                                    {/* Mindgain Link */}
                                    <a 
                                        href="https://mindgaintherapies.com" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between p-4 bg-[#1D6B6B]/5 rounded-xl mb-6 hover:bg-[#1D6B6B]/10 transition-colors group"
                                        data-testid="mindgain-website-link"
                                    >
                                        <div>
                                            <p className="font-medium text-[#1D6B6B]">Mindgain Therapies</p>
                                            <p className="text-sm text-[#7D8E95]">Learn more about our hypnotherapy services</p>
                                        </div>
                                        <ExternalLink className="w-5 h-5 text-[#7D8E95] group-hover:text-[#1D6B6B] transition-colors" />
                                    </a>

                                    {/* Enquiry Form */}
                                    <div className="border-t border-white/40/20 pt-6">
                                        <Heading level={4} className="text-[#1D6B6B] mb-2">
                                            Get in Touch
                                        </Heading>
                                        <SmallText className="text-[#7D8E95] mb-4 block">
                                            Interested in exploring hypnotherapy? Fill in your details and a therapist will be in touch.
                                        </SmallText>
                                        
                                        <form onSubmit={handleEnquirySubmit} className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Your Name *</label>
                                                <input
                                                    type="text"
                                                    required
                                                    value={enquiryForm.name}
                                                    onChange={(e) => setEnquiryForm({...enquiryForm, name: e.target.value})}
                                                    className="w-full px-4 py-3 rounded-xl bg-white/70 border border-white/40/30 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                                                    placeholder="Enter your name"
                                                    data-testid="enquiry-name"
                                                />
                                            </div>
                                            
                                            <div>
                                                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Email Address *</label>
                                                <input
                                                    type="email"
                                                    required
                                                    value={enquiryForm.email}
                                                    onChange={(e) => setEnquiryForm({...enquiryForm, email: e.target.value})}
                                                    className="w-full px-4 py-3 rounded-xl bg-white/70 border border-white/40/30 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                                                    placeholder="your@email.com"
                                                    data-testid="enquiry-email"
                                                />
                                            </div>
                                            
                                            <div>
                                                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Phone (optional)</label>
                                                <input
                                                    type="tel"
                                                    value={enquiryForm.phone}
                                                    onChange={(e) => setEnquiryForm({...enquiryForm, phone: e.target.value})}
                                                    className="w-full px-4 py-3 rounded-xl bg-white/70 border border-white/40/30 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                                                    placeholder="Your phone number"
                                                    data-testid="enquiry-phone"
                                                />
                                            </div>
                                            
                                            <div>
                                                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">What would you like support with?</label>
                                                <textarea
                                                    value={enquiryForm.concerns}
                                                    onChange={(e) => setEnquiryForm({...enquiryForm, concerns: e.target.value})}
                                                    rows={3}
                                                    className="w-full px-4 py-3 rounded-xl bg-white/70 border border-white/40/30 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30 resize-none"
                                                    placeholder="e.g., anxiety, emotional regulation, trauma, limiting beliefs..."
                                                    data-testid="enquiry-concerns"
                                                />
                                            </div>
                                            
                                            <div>
                                                <label className="block text-sm font-medium text-[#1D6B6B] mb-2">Preferred contact method</label>
                                                <div className="flex gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => setEnquiryForm({...enquiryForm, preferredContact: 'email'})}
                                                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                                                            enquiryForm.preferredContact === 'email'
                                                                ? 'bg-[#1D6B6B] text-white'
                                                                : 'bg-white/70 text-[#7D8E95]'
                                                        }`}
                                                    >
                                                        Email
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setEnquiryForm({...enquiryForm, preferredContact: 'phone'})}
                                                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                                                            enquiryForm.preferredContact === 'phone'
                                                                ? 'bg-[#1D6B6B] text-white'
                                                                : 'bg-white/70 text-[#7D8E95]'
                                                        }`}
                                                    >
                                                        Phone
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#D07848] to-[#E89060] text-white py-3 px-6 rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                                                data-testid="submit-enquiry-btn"
                                            >
                                                {isSubmitting ? (
                                                    <>Processing...</>
                                                ) : (
                                                    <>
                                                        <Send className="w-4 h-4" />
                                                        Submit Enquiry
                                                    </>
                                                )}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            ) : (
                                /* Success State */
                                <div className="p-8 text-center">
                                    <div className="w-20 h-20 rounded-full bg-[#81C784]/20 flex items-center justify-center mx-auto mb-6">
                                        <Check className="w-10 h-10 text-[#81C784]" />
                                    </div>
                                    <Heading level={2} className="text-[#1D6B6B] mb-3">
                                        Enquiry Submitted!
                                    </Heading>
                                    <BodyText className="text-[#7D8E95] mb-6">
                                        Thank you for reaching out. A therapist from Mindgain Therapies will be in touch within 24-48 hours.
                                    </BodyText>
                                    <a 
                                        href="https://mindgaintherapies.com" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 bg-[#1D6B6B] text-white py-3 px-6 rounded-xl font-medium hover:bg-[#145555] transition-colors"
                                        data-testid="visit-mindgain-btn"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        Visit Mindgain Therapies
                                    </a>
                                    <button
                                        onClick={() => setShowEnquiryModal(false)}
                                        className="block w-full mt-4 text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Playing screen
    const Icon = meditation.icon;
    const progress = ((currentSegment + 1) / meditation.script.length) * 100;

    return (
        <div className="min-h-screen" style={{ backgroundColor: meditation.bgColor }}>
            <audio ref={audioRef} />
            <audio ref={musicRef} src="/audio/ocean-peace.mp3" loop preload="auto" />
            
            <header className="sticky top-0 z-40 p-4" style={{ backgroundColor: meditation.bgColor + 'E6' }}>
                <div className="flex items-center justify-between">
                    <IconButton onClick={handleBack} data-testid="back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <SmallText className="text-[#7D8E95]">{meditation.name}</SmallText>
                    <div className="w-10" />
                </div>
                
                {/* Audio controls */}
                <div className="mt-3 flex items-center justify-center gap-2">
                    <button
                        onClick={() => {
                            setMusicEnabled(!musicEnabled);
                            if (musicRef.current) {
                                if (!musicEnabled && isPlaying) {
                                    musicRef.current.play().catch(() => {});
                                } else {
                                    musicRef.current.pause();
                                }
                            }
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                            musicEnabled 
                                ? 'bg-white/50 text-[#7D8E95]' 
                                : 'bg-white/30 text-[#7D8E95]'
                        }`}
                        data-testid="music-toggle"
                    >
                        <Volume2 className="w-4 h-4" />
                        <SmallText>{musicEnabled ? 'Music On' : 'Music Off'}</SmallText>
                    </button>
                </div>
                
                {/* Progress bar */}
                <div className="mt-3 h-1.5 bg-white/50 rounded-full overflow-hidden">
                    <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${progress}%`, backgroundColor: meditation.color }}
                    />
                </div>
            </header>

            <main className="max-w-md mx-auto px-6 py-12 flex flex-col items-center justify-center min-h-[60vh]">
                {/* Icon animation */}
                <div 
                    className={`w-32 h-32 rounded-full flex items-center justify-center mb-8 ${isPlaying && !isPaused ? 'animate-pulse' : ''}`}
                    style={{ backgroundColor: meditation.color + '30' }}
                >
                    <Icon className="w-16 h-16" style={{ color: meditation.color }} />
                </div>

                {/* Current text */}
                <div className="text-center mb-8">
                    {meditation.script[currentSegment] && (
                        <BodyText className="text-[#1D6B6B] text-lg leading-relaxed">
                            {meditation.script[currentSegment].text}
                        </BodyText>
                    )}
                </div>

                {/* Loading indicator */}
                {isLoadingAudio && (
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-3 h-3 rounded-full animate-pulse" style={{ backgroundColor: meditation.color }} />
                        <SmallText className="text-[#7D8E95]">Loading audio...</SmallText>
                    </div>
                )}

                {/* Controls */}
                <div className="flex flex-col items-center gap-4">
                    {!isPlaying && (
                        <AudioTip variant="meditation" className="max-w-sm" />
                    )}
                    
                    {!isPlaying ? (
                        <PrimaryButton 
                            onClick={handleStart} 
                            size="lg" 
                            data-testid="start-btn"
                            style={{ backgroundColor: meditation.color }}
                        >
                            <Play className="w-5 h-5 mr-2" />
                            Begin
                        </PrimaryButton>
                    ) : (
                        <>
                            <IconButton onClick={handleReset} className="bg-white/50" data-testid="reset-btn">
                                <RotateCcw className="w-5 h-5" />
                            </IconButton>
                            <PrimaryButton 
                                onClick={handlePause} 
                                data-testid="pause-btn"
                                style={{ backgroundColor: meditation.color }}
                            >
                                {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                            </PrimaryButton>
                        </>
                    )}
                </div>

                {/* Segment indicator */}
                <div className="flex justify-center gap-1 mt-8 flex-wrap max-w-xs">
                    {meditation.script.map((_, index) => (
                        <div
                            key={index}
                            className="w-2 h-2 rounded-full transition-all"
                            style={{ 
                                backgroundColor: index <= currentSegment ? meditation.color : meditation.color + '40',
                                transform: index === currentSegment ? 'scale(1.3)' : 'scale(1)'
                            }}
                        />
                    ))}
                </div>
            </main>
        </div>
    );
};

export default GuidedMeditations;
